import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router } from '@angular/router';
import { catchError, map, of, switchMap, type Observable } from 'rxjs';
import { ApiService } from '../services/api.service';
import { AuthService } from '../services/auth.service';
import { CABINET_PERMISSION } from '../utils/auth-permissions';
import { getPostAuthRoute } from '../utils/auth-routing';

/**
 * BE-1484: короткий адрес `/scan` — сканер без id бизнеса в адресе. Для закладки на рабочем
 * телефоне и для ярлыка «Сканер» у установленного кабинета (`shortcuts` в манифесте).
 *
 * Сам маршрут ничего не показывает: гвард всегда уводит, и всегда с заменой адреса — иначе
 * «Назад» вернул бы на `/scan`, а он снова увёл бы вперёд. Поэтому развилку ролей
 * `businessCabinetGuard` он повторяет, а не зовёт: тот уводит через `router.navigate`, и
 * `/scan` остался бы в истории под экраном входа. Внутренних ссылок на `/scan` не ставить:
 * замена адреса затрёт страницу, с которой на него пришли.
 *
 * - не вошёл → вход;
 * - бухгалтер и админский контур → их обычный стартовый маршрут;
 * - бизнес-контур → сканер, если активный бизнес с правом на сканер ровно один, иначе список
 *   бизнесов. Неактивные не считаются: сканирование у них бэкенд не пропустит.
 */
export const scanShortcutGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const api = inject(ApiService);
  const router = inject(Router);
  const redirect = (commands: string[]) =>
    new RedirectCommand(router.createUrlTree(commands), { replaceUrl: true });

  if (!auth.authState().isAuthenticated) {
    return redirect(['/auth']);
  }
  if (!auth.isBusinessContour()) {
    return redirect(getPostAuthRoute(auth.role()) ?? ['/auth']);
  }

  // Права из кэша прошлой сессии считаем загруженными. Нет их вовсе — перечитываем сами:
  // на «прав не знаем» hasCabinetPermission отвечает «да» на любой ключ.
  const permissionsLoaded$: Observable<unknown> =
    auth.permissions() == null ? auth.getCurrentUser() : of(null);
  return permissionsLoaded$.pipe(
    switchMap(() => api.getBusinessesForCurrentUser()),
    map((businesses) => {
      // Ответ /auth/me без прав — право выданным не считаем: список пуст.
      const scanners = businesses.filter(
        (item) =>
          auth.permissions() != null &&
          item.status === 'ACTIVE' &&
          auth.canInCabinet(CABINET_PERMISSION.scannerScan, item.id),
      );
      return redirect(
        scanners.length === 1
          ? ['/businesses', scanners[0].id, 'scan']
          : ['/businesses'],
      );
    }),
    // Список бизнесов сам покажет ошибку и «Повторить».
    catchError(() => of(redirect(['/businesses']))),
  );
};
